import { useState, useRef, useEffect } from 'react';
import { roleLabel } from '@supernova/shared';
import { User, LogOut, ChevronDown, Menu, Building2, Check, PlusCircle, Warehouse, Store } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { accessibleLocations } from '@/lib/location-access';
import { useLogout } from '@/hooks/use-auth';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { isWarehouseType } from '@/lib/location-type';

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-red-100 text-red-700',
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  manager: 'bg-teal-100 text-teal-700',
  employee: 'bg-gray-100 text-gray-700',
  inventory_manager: 'bg-amber-100 text-amber-700',
};

// Icon helper: warehouse rows get a Warehouse icon, regular store/company rows
// get a Store icon. Used in the location picker so the user can tell at a
// glance which entries are inhouse warehouses (those POs flow differently —
// stock-checked at send, deducted from the warehouse's inventory).
export function LocationTypeIcon({ type, className = 'h-3.5 w-3.5' }: { type?: string; className?: string }) {
  if (isWarehouseType(type)) {
    return <Warehouse className={`${className} text-orange-600`} aria-label="Warehouse" />;
  }
  return <Store className={`${className} text-primary`} aria-label="Store" />;
}

function LocationPicker({ tenantName }: { tenantName?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { user, tenant } = useAuthStore();
  const { data } = useLocations();
  const allLocations = data?.data ?? [];
  const { selectedLocationId, selectedLocationName, _hasHydrated, setSelectedLocation } = useLocationStore();

  // Owner and super_admin always see every location. Admin, manager and
  // employee are scoped to their assigned locations — except an admin with
  // no explicit assignments, who keeps tenant-wide visibility. The rule lives
  // in @/lib/location-access so the product form's inventory tables and the
  // products service apply exactly the same one.
  const locations = accessibleLocations(user, allLocations);

  // Auto-select first location if none selected or current is invalid.
  // Wait for Zustand persist hydration so we don't overwrite the saved
  // location with locations[0] before the persisted value loads.
  useEffect(() => {
    if (!_hasHydrated || locations.length === 0) return;
    const currentValid = locations.some((l: any) => l.id === selectedLocationId);
    if (!selectedLocationId || !currentValid) {
      setSelectedLocation(locations[0].id, locations[0].name);
    }
  }, [_hasHydrated, locations, selectedLocationId, setSelectedLocation]);

  // Re-sync name if it changed (e.g. location was renamed)
  useEffect(() => {
    if (selectedLocationId && locations.length > 0) {
      const match = locations.find((l: any) => l.id === selectedLocationId);
      if (match && match.name !== selectedLocationName) {
        setSelectedLocation(match.id, match.name);
      }
    }
  }, [locations, selectedLocationId, selectedLocationName, setSelectedLocation]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLocation = locations.find((l: any) => l.id === selectedLocationId);
  const displayName = selectedLocation?.nickname || selectedLocationName || tenantName || 'Select location';

  // Users missed that this is a dropdown when it was plain text, so the trigger
  // is styled as a bordered select with an explicit "Switch location" label
  // whenever there is more than one location to switch between.
  const canSwitch = locations.length > 1;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={canSwitch ? 'Switch location' : 'Location'}
        className={`group flex items-center gap-2 rounded-lg border px-2 py-1 text-left transition-colors ${
          open
            ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
            : 'border-primary/30 bg-primary/5 hover:border-primary/60 hover:bg-primary/10'
        }`}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
          <Building2 className="h-4 w-4 text-primary" />
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
            {canSwitch ? 'Switch location' : 'Location'}
          </span>
          <span className="max-w-[80px] truncate text-sm font-medium text-foreground sm:max-w-[160px] lg:max-w-[280px]">
            {displayName}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-primary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[240px] rounded-lg border border-border bg-white py-1 shadow-lg">
          {locations.length > 0 && (
            <div className="px-4 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Your locations ({locations.length})
            </div>
          )}
          {locations.map((loc: any, idx: number) => {
            // Insert a divider between the last store and the first warehouse
            const prevLoc = idx > 0 ? locations[idx - 1] : null;
            const showDivider = prevLoc && !isWarehouseType(prevLoc.type) && isWarehouseType(loc.type);
            return (
              <div key={loc.id}>
                {showDivider && <div className="my-1 border-t border-border" />}
                <button
                  onClick={() => {
                    setSelectedLocation(loc.id, loc.name);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted ${
                    selectedLocationId === loc.id ? 'font-medium text-primary' : 'text-foreground'
                  }`}
                >
                  <Check
                    className={`h-3.5 w-3.5 shrink-0 ${selectedLocationId === loc.id ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <LocationTypeIcon type={loc.type} className="h-3.5 w-3.5 shrink-0" />
                  <div className="flex min-w-0 flex-col">
                    {loc.nickname ? (
                      <>
                        <span className="truncate text-sm">{loc.nickname}</span>
                        <span className="truncate text-xs text-muted-foreground">"{loc.name}"</span>
                      </>
                    ) : (
                      <span className="truncate text-sm">{loc.name}</span>
                    )}
                  </div>
                  {loc.isActive === false && (
                    <span className="ml-auto shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                      Pending
                    </span>
                  )}
                </button>
              </div>
            );
          })}
          {(user?.role === 'owner' || user?.role === 'super_admin') &&
            // Hidden entirely unless the super admin has explicitly enabled the
            // gate. `canAddLocations` defaults to false in the DB — undefined
            // (e.g. legacy payload) is also treated as disabled so the gate is
            // closed by default everywhere.
            tenant?.canAddLocations === true && (
              <div className={locations.length > 0 ? 'mt-1 border-t border-border pt-1' : ''}>
                {tenant?.isActive === false ? (
                  <span
                    title="Your business activation is pending. Add Company will be available once activated."
                    className="flex w-full cursor-not-allowed items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground opacity-50"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    Add Company
                  </span>
                ) : (
                  <a
                    href="#/add-company"
                    onClick={() => setOpen(false)}
                    className="flex w-full items-center gap-2 px-4 py-2 text-xs font-medium text-primary hover:bg-muted"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    Add Company
                  </a>
                )}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const { user, tenant } = useAuthStore();
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleMobile = useSidebarStore((s) => s.toggleMobile);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-white px-4 lg:px-6">
      {/* Hamburger button -- visible only on mobile (below lg) */}
      <button
        type="button"
        onClick={toggleMobile}
        className="inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
        aria-label="Toggle navigation menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Spacer for desktop -- keeps right alignment of user menu */}
      <div className="hidden lg:block" />

      {/* Location picker + User menu */}
      <div className="flex items-center gap-3">
        {user?.role !== 'super_admin' && (
          <div className="border-r border-border pr-3">
            <LocationPicker tenantName={tenant?.name} />
          </div>
        )}

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-muted"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <User className="h-4 w-4" />
            </div>
            {/* Hide name + role on mobile, show on sm+ */}
            <div className="hidden text-left sm:block">
              <div className="text-sm font-medium">{user?.name || 'User'}</div>
              {user?.role && (
                <span
                  className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${ROLE_COLORS[user.role] || ROLE_COLORS.employee}`}
                >
                  {user.customRole?.name ?? roleLabel(user.role)}
                </span>
              )}
            </div>
            <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-border bg-white py-1 shadow-lg">
              <div className="border-b border-border px-4 py-2">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout.mutate();
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
